import React from "react";
import { useOdooLiveChatBridge } from "hooks/use-odoo-live-chat-bridge/use-odoo-live-chat-bridge";
import { useOdooLiveChatConnect } from "hooks/use-odoo-live-chat-connect/use-odoo-live-chat-connect";
import { useOdooLiveChatStatus } from "hooks/use-odoo-live-chat-status/use-odoo-live-chat-status";
import { useOdooLiveChatTabAlert } from "hooks/use-odoo-live-chat-tab-alert/use-odoo-live-chat-tab-alert";
import { useScrollCollapse } from "hooks/use-scroll-collapse/use-scroll-collapse";

import { OdooLiveChatClosedPanel } from "./odoo-live-chat-closed-panel";
import { OdooLiveChatPill } from "./odoo-live-chat-pill";
import { OdooLiveChatStatusPanel } from "./odoo-live-chat-status-panel";

// Pill launcher, shown on the Bimbel pages and, once it has been shown or the
// chat opened in this tab, on every page. It collapses to the Coco circle on
// scroll down and expands on scroll up or when the page is idle. The Odoo
// widget is loaded on the first click; while it loads, or when nobody is
// available, a panel shaped like the chat window explains the state. Hidden
// while the Odoo chat window is open; a minimized chat is resumed from this
// launcher (the stock Odoo bubble is hidden by the Odoo module), which also
// shows the unread count.
interface IOdooLiveChatLauncherProps {
  isBimbelPage: boolean;
}

export const OdooLiveChatLauncher: React.FC<IOdooLiveChatLauncherProps> = ({
  isBimbelPage
}) => {
  const { isVisible, hasOpenedChat, bridgeAvailable, unreadCount, markOpened } =
    useOdooLiveChatBridge(isBimbelPage);
  useOdooLiveChatTabAlert(unreadCount);

  // Outside Bimbel the launcher only appears to resume a minimized chat.
  const isShown = isVisible && (isBimbelPage || hasOpenedChat);

  const { serverStatus, serverAvailable, refreshFresh } =
    useOdooLiveChatStatus(isShown);
  const isCollapsed = useScrollCollapse();
  const { panel, handleClick, handleRetry, closePanel } =
    useOdooLiveChatConnect({
      isShown,
      serverStatus,
      serverAvailable,
      refreshFresh,
      markOpened
    });

  if (!isShown) return null;

  if (panel === "closed") {
    return (
      <OdooLiveChatClosedPanel
        messages={serverStatus?.offline?.messages ?? []}
        canLeaveMessage={Boolean(serverStatus?.offline?.can_leave_message)}
        onClose={closePanel}
      />
    );
  }

  if (panel) {
    return (
      <OdooLiveChatStatusPanel
        variant={panel}
        onClose={closePanel}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <OdooLiveChatPill
      // Availability from the bridge once the widget is loaded; before that,
      // from the cached status endpoint.
      isAvailable={bridgeAvailable ?? serverAvailable}
      unreadCount={unreadCount}
      isCollapsed={isCollapsed}
      onClick={handleClick}
    />
  );
};
